import React, { useCallback, useMemo, useState } from "react";
import AsyncActionButton from "./AsyncActionButton";
import { useLanguage } from "../i18n/LanguageContext";
import { detectShoppingCategory, normalizeShoppingCategory, normalizeShoppingUnit } from "../services/shoppingOcrService";
import "../styles/scanModal.css";

const toItem = (item, categories = [], units = []) => {
  if (item && typeof item === "object") {
    const ad = String(item.ad || item.name || item.urunAdi || "").trim();
    return {
      ad,
      miktar: String(item.miktar || item.quantity || "1"),
      birim: normalizeShoppingUnit(item.birim || item.unit || "adet", units),
      kategori: normalizeShoppingCategory(item.kategori || item.category || detectShoppingCategory(ad, categories), categories),
    };
  }
  const ad = String(item || "").trim();
  return {
    ad,
    miktar: "1",
    birim: normalizeShoppingUnit("adet", units),
    kategori: normalizeShoppingCategory(detectShoppingCategory(ad, categories), categories),
  };
};

/**
 * Alışveriş listesi tarama sonuçlarını gösteren modal.
 * Kullanıcı ürün adı, miktar, birim ve kategoriyi onaylamadan önce düzenleyebilir.
 */
export default function ScanPreviewModal({
  items = [],
  categories = [],
  units = [],
  onConfirm,
  onClose,
}) {
  const { t, lang } = useLanguage();

  const normalizedItems = useMemo(
    () => items.map((item) => toItem(item, categories, units)),
    [items, categories, units]
  );

  const [secilen, setSecilen] = useState(() =>
    Object.fromEntries(normalizedItems.map((_, i) => [i, true]))
  );
  const [duzenlenen, setDuzenlenen] = useState(() =>
    Object.fromEntries(normalizedItems.map((item, i) => [i, item]))
  );

  const categoryOptions = useMemo(
    () => (categories || []).filter(Boolean).filter((x) => !/^tümü$|^all$/i.test(String(x))),
    [categories]
  );
  const unitOptions = useMemo(
    () => (units && units.length ? units : ["adet", "kg", "lt", "paket"]),
    [units]
  );

  const seciliSayisi = useMemo(
    () => Object.values(secilen).filter(Boolean).length,
    [secilen]
  );

  const tumunuSec = () =>
    setSecilen(Object.fromEntries(normalizedItems.map((_, i) => [i, true])));
  const secimiKaldir = () =>
    setSecilen(Object.fromEntries(normalizedItems.map((_, i) => [i, false])));

  const handleToggle = useCallback((idx) => {
    setSecilen((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }, []);

  const updateItem = useCallback((idx, patch) => {
    setDuzenlenen((prev) => ({
      ...prev,
      [idx]: { ...(prev[idx] || {}), ...patch },
    }));
  }, []);

  const handleNameChange = useCallback((idx, value) => {
    const nextName = value;
    const nextCategory = detectShoppingCategory(nextName, categoryOptions);
    updateItem(idx, { ad: nextName, kategori: nextCategory });
  }, [categoryOptions, updateItem]);

  const handleConfirm = async () => {
    const sonuc = normalizedItems
      .map((_, i) => ({ ...(duzenlenen[i] || {}), secili: secilen[i] }))
      .filter((x) => x.secili && String(x.ad || "").trim().length > 0)
      .map((x) => ({
        ad: String(x.ad || "").trim(),
        miktar: String(x.miktar || "1").trim() || "1",
        birim: normalizeShoppingUnit(x.birim || "adet", unitOptions),
        kategori: normalizeShoppingCategory(x.kategori || detectShoppingCategory(x.ad, categoryOptions), categoryOptions),
      }));
    if (sonuc.length > 0) return onConfirm(sonuc);
    return undefined;
  };

  return (
    <div className="scanModalOverlay" onClick={onClose}>
      <div className="scanModalCard" onClick={(e) => e.stopPropagation()}>
        <div className="scanModalHeader">
          <div className="scanModalTitle">
            <span className="scanModalEmoji">📋</span>
            <div>
              <strong>{t("scan.previewTitle")}</strong>
              <p>{items.length} {lang === "en" ? "item detected" : "ürün tespit edildi"}</p>
            </div>
          </div>
          <button className="scanModalClose" onClick={onClose}>✕</button>
        </div>

        <div className="scanBulkRow">
          <button className="scanBulkBtn" onClick={tumunuSec}>
            {lang === "en" ? "Select All" : "Tümünü Seç"}
          </button>
          <button className="scanBulkBtn" onClick={secimiKaldir}>
            {lang === "en" ? "Deselect All" : "Seçimi Kaldır"}
          </button>
        </div>

        <div className="scanItemList shoppingScanItemList">
          {normalizedItems.map((_, idx) => {
            const item = duzenlenen[idx] || normalizedItems[idx];
            return (
              <div
                key={idx}
                className={`scanItem shoppingScanItem${secilen[idx] ? " selected" : ""}`}
              >
                <button
                  type="button"
                  className={`scanCheckbox${secilen[idx] ? " checked" : ""}`}
                  onClick={() => handleToggle(idx)}
                  aria-label={lang === "en" ? "Select item" : "Ürünü seç"}
                >
                  {secilen[idx] ? "✓" : ""}
                </button>

                <div className="shoppingScanFields">
                  <input
                    className="scanItemInput shoppingScanNameInput"
                    value={item.ad ?? ""}
                    onChange={(e) => handleNameChange(idx, e.target.value)}
                    placeholder={lang === "en" ? "Item name" : "Ürün adı"}
                  />
                  <div className="shoppingScanMetaRow">
                    <input
                      className="shoppingScanQtyInput"
                      value={item.miktar ?? "1"}
                      inputMode="decimal"
                      onChange={(e) => updateItem(idx, { miktar: e.target.value })}
                      aria-label={lang === "en" ? "Quantity" : "Miktar"}
                    />
                    <select
                      className="shoppingScanSelect shoppingScanUnitSelect"
                      value={item.birim || "adet"}
                      onChange={(e) => updateItem(idx, { birim: normalizeShoppingUnit(e.target.value, unitOptions) })}
                      aria-label={lang === "en" ? "Unit" : "Birim"}
                    >
                      {unitOptions.map((unit) => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                    <select
                      className="shoppingScanSelect shoppingScanCategorySelect"
                      value={item.kategori || categoryOptions[0] || "Market"}
                      onChange={(e) => updateItem(idx, { kategori: e.target.value })}
                      aria-label={lang === "en" ? "Category" : "Kategori"}
                    >
                      {categoryOptions.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="scanModalFooter">
          <span className="scanSelectedCount">
            {seciliSayisi} {lang === "en" ? "selected" : "seçildi"}
          </span>
          <div className="scanFooterBtns">
            <button className="darkSecondaryBtn" onClick={onClose}>
              {lang === "en" ? "Cancel" : "İptal"}
            </button>
            <AsyncActionButton
              className="primaryButton"
              onAction={handleConfirm}
              disabled={seciliSayisi === 0}
            >
              {lang === "en" ? `Add ${seciliSayisi} Item${seciliSayisi > 1 ? "s" : ""}` : `${seciliSayisi} Ürün Ekle`}
            </AsyncActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}
